/**
 * Who can actually be charged — the READ-SIDE MIRROR of the server's
 * resolveChargeTarget (`functions/billingCharge.js`).
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * The admin billing panel used to answer "does this family have a payment
 * method?" with `Boolean(profile.paymentMethod)`. That predicate is not the one
 * the charge run uses, and on 2026-08-31 — the night the first September
 * invoices were generated — the two disagreed badly: the panel reported
 * **9 of 59** families with a method while the charge run could actually bill
 * **28 of 32** invoiced families. Twenty families displayed a black `none`
 * badge and were fully chargeable (Rebecca Charles was the worked example).
 *
 * The cause is the resolution ORDER. A family with no portal method resolves
 * to `chargePaymentMethod`, the card mapped over from the MMS season, and that
 * branch never consults `autopayStatus` or `paymentMethod` at all. (Since
 * 2026-10-01 a portal method, when chargeable, is checked BEFORE the mapped
 * card; see the server header for why.) Any client-side count
 * built on either of those fields therefore understates readiness by exactly
 * the number of families still on a mapped MMS card, which on 2026-08-31 was
 * 20 of the 28 chargeable.
 *
 * That is not a cosmetic gap: the panel's count is what a human reads when
 * deciding whether the studio is ready to charge, and it read as "we are
 * nowhere near ready" on a night when it was.
 *
 * KEEPING THE TWO SIDES TOGETHER
 * ------------------------------
 * This is the same shape as the teacher-caps trap in CLAUDE.md — "if only one
 * side moves, the board shows a cap the generator does not enforce". A genuine
 * shared module is not available here: CRA's module scope forbids importing
 * from `functions/`, and the Functions deploy packages only `functions/`. So
 * this file is a deliberate mirror, and `chargeTarget.test.js` pins it two
 * ways: a fixture table covering every branch, and a parity guard that reads
 * `functions/billingCharge.js` as SOURCE TEXT and fails if the server's branch
 * order changes without this copy following.
 *
 * DISPLAY ONLY. Nothing here decides what is charged — the server re-derives
 * the plan, and `performPlanCharges` remains the single authority on money.
 */

/**
 * Mirror of functions/billingCharge.js → resolveChargeTarget.
 * Shape is kept identical to the server's return value on purpose: an
 * identical shape is what makes the parity test meaningful.
 *
 * Returns one of:
 *   { kind: 'external', note }
 *   { kind: 'none' }
 *   { kind: 'target', customerId, paymentMethodId, brand, last4, source }
 */
export function resolveChargeTarget(profile) {
	if (!profile) return { kind: 'none' };
	if (profile.chargePolicy === 'external') {
		return { kind: 'external', note: profile.chargePolicyNote || null };
	}
	// Portal consent first, mirroring the server (reversed 2026-10-01).
	const pm = profile.paymentMethod;
	if (profile.autopayStatus === 'method_on_file' && pm && pm.id && profile.stripeCustomerId) {
		return {
			kind: 'target',
			customerId: profile.stripeCustomerId,
			paymentMethodId: pm.id,
			brand: pm.brand || pm.bankName || null,
			last4: pm.last4 || null,
			source: 'portal_consent',
		};
	}
	const mapped = profile.chargePaymentMethod;
	if (mapped && mapped.id && mapped.customerId) {
		return {
			kind: 'target',
			customerId: mapped.customerId,
			paymentMethodId: mapped.id,
			brand: mapped.brand || null,
			last4: mapped.last4 || null,
			source: mapped.source || 'mapped',
		};
	}
	return { kind: 'none' };
}

/**
 * The badge/sort vocabulary for one family row, derived from the resolved
 * target rather than from any single profile field.
 *
 * `pending_verification` is surfaced separately even though it resolves to
 * `kind: 'none'`: an ACH micro-deposit that has not cleared genuinely CANNOT
 * be charged yet, so it belongs outside the chargeable count — but it is not
 * the same work item as a family who never added anything, and collapsing the
 * two would put it on the wrong end of the chase list.
 *
 * `rank` orders the Method column under 'desc' as who-needs-chasing-first:
 * missing (3) → verifying (2) → external (1) → chargeable (0).
 */
export function methodDisplay(profile) {
	const target = resolveChargeTarget(profile);

	if (target.kind === 'target') {
		// A mapped MMS-season card and a method the family added in the portal
		// are both chargeable, but they are NOT the same fact about consent —
		// on 2026-08-31, 20 of 28 chargeable families were on a mapped card and
		// the panel gave no way to see that. Keep them visually distinct.
		const mapped = target.source !== 'portal_consent';
		return {
			kind: 'target',
			chargeable: true,
			rank: 0,
			variant: 'success',
			label: mapped ? 'mapped' : 'on file',
			title: [
				mapped ? 'Mapped card — not portal consent' : 'Added by the family in the portal',
				[target.brand, target.last4 && `••${target.last4}`].filter(Boolean).join(' '),
				`source: ${target.source}`,
			]
				.filter(Boolean)
				.join(' · '),
		};
	}

	if (target.kind === 'external') {
		return {
			kind: 'external',
			chargeable: false,
			rank: 1,
			variant: 'neutral',
			label: 'external',
			title: target.note
				? `Paid outside Stripe — never charged · ${target.note}`
				: 'Paid outside Stripe — never charged',
		};
	}

	if (profile && profile.autopayStatus === 'pending_verification') {
		return {
			kind: 'verifying',
			chargeable: false,
			rank: 2,
			variant: 'warning',
			label: 'verifying',
			title: 'Micro-deposits not yet verified — cannot be charged until they clear',
		};
	}

	return {
		kind: 'none',
		chargeable: false,
		rank: 3,
		variant: 'neutral',
		label: 'none',
		title: 'No chargeable payment method on file',
	};
}

/**
 * Readiness for ONE billing month, counted over the families that actually
 * have an invoice in it.
 *
 * WHY THE DENOMINATOR IS INVOICES AND NOT THE ROSTER
 * --------------------------------------------------
 * This is the second half of the 2026-08-31 defect. Fixing the payment-method
 * predicate moved the header from "9/59" to "28/59", which is a true number
 * answering the wrong question: the 27 families with no September invoice are
 * not unprepared, they are not being billed. The figure a human reads before
 * moving money has to describe the run that is about to happen — 28 of 32.
 *
 * Takes ALL invoices for the month (any status) so the caller can tell "not
 * generated yet" from "generated then voided": both yield zero open invoices
 * and mean opposite things.
 */
export function summarizeReadiness(periodInvoices, profilesById) {
	const invoices = Array.isArray(periodInvoices) ? periodInvoices : [];
	const profiles = profilesById || {};
	const counts = { chargeable: 0, external: 0, verifying: 0, none: 0 };
	const seen = new Set();

	invoices
		.filter((inv) => inv && inv.status === 'open')
		.forEach((inv) => {
			// One row per FAMILY. A family holding two open invoices for the
			// month is still one payment method and one decision, and counting
			// it twice would overstate both halves of the ratio.
			if (!inv.familyId || seen.has(inv.familyId)) return;
			seen.add(inv.familyId);
			const d = methodDisplay(profiles[inv.familyId]);
			if (d.chargeable) counts.chargeable += 1;
			else counts[d.kind] += 1;
		});

	return { ...counts, invoiced: seen.size, generated: invoices.length };
}
